import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Container, Divider, Drawer, IconButton, Skeleton, TextField, Typography, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import { Add, Minus, SearchNormal1 } from 'iconsax-react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getFaqsByEventIdForRegistrationAsync } from '@application/features/faqCategory/faqCategoryThunk';
import { useEventRegistrationLanguageContext } from './EventRegistrationLanguageContext';
import { between, centered } from '@ui/style/style';
import { eventRegistrationDrawerCloseButtonSx, eventRegistrationDrawerSlideProps, getEventRegistrationDrawerContentHeight, getEventRegistrationDrawerPaperProps } from './eventRegistrationDrawerStyles';
import { useEventRegistrationTranslation } from './hooks/useEventRegistrationTranslation';
import EmbeddedPreviewSheet from './EmbeddedPreviewSheet';
const faqAccordionCardStyle = {
    boxShadow: '0px 40px 56px 0px #00000014, 0px 16px 40px 0px #0000000F, 0px 4px 8px 0px #0000000A',
    background: '#FFFFFF',
    marginBottom: '8px',
    borderRadius: '8px',
    border: '1px solid #00244D33'
};
const EventRegistrationFaqsDrawer = ({ open, onClose, onContactUsOpen, embeddedPreview = false, previewLayout }) => {
    const theme = useTheme();
    const { t } = useEventRegistrationTranslation();
    const dispatch = useAppDispatch();
    const { e } = useParams();
    const { selectedLanguageId } = useEventRegistrationLanguageContext();
    const registrationCategories = useAppSelector((state) => state.faqCategory.registrationCategories);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedIndex, setExpandedIndex] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        if (!open) {
            setSearchQuery('');
            setExpandedIndex(false);
            return;
        }
        const loadFaqs = async () => {
            if (!e || !selectedLanguageId)
                return;
            setIsLoading(true);
            try {
                await dispatch(getFaqsByEventIdForRegistrationAsync({
                    eventId: e,
                    languageId: selectedLanguageId
                })).unwrap();
            }
            finally {
                setIsLoading(false);
            }
        };
        void loadFaqs();
    }, [dispatch, e, open, selectedLanguageId]);
    const faqData = useMemo(() => (registrationCategories ?? []).map((category) => ({
        title: category.name,
        faqs: (category.questions ?? []).map((q) => ({
            id: q.id,
            question: q.question,
            answer: q.answer
        }))
    })), [registrationCategories]);
    const filteredFaqData = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query)
            return faqData;
        return faqData
            .map((section) => {
            const filteredFaqs = section.faqs.filter((faq) => faq.question.toLowerCase().includes(query) || faq.answer.toLowerCase().includes(query));
            if (filteredFaqs.length === 0)
                return null;
            return { ...section, faqs: filteredFaqs };
        })
            .filter((section) => Boolean(section));
    }, [faqData, searchQuery]);
    const showLoadingSkeleton = isLoading && faqData.length === 0;
    const content = (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                    display: 'flex',
                    justifyContent: 'flex-end',
                    px: { xs: 2, sm: 1 },
                    pt: { xs: 2, sm: 1 },
                    pb: 1
                }, children: _jsx(IconButton, { onClick: onClose, "aria-label": t('actions.close'), sx: eventRegistrationDrawerCloseButtonSx, children: _jsx(Icon, { icon: 'mdi:close', fontSize: 16 }) }) }), _jsxs(Container, { sx: {
                    height: getEventRegistrationDrawerContentHeight(embeddedPreview),
                    mb: '8px',
                    overflowY: 'auto',
                    px: { xs: 2, sm: 3 },
                    pb: 3
                }, children: [_jsxs(Box, { sx: {
                            ...centered,
                            flexDirection: 'column',
                            mb: 0,
                            gap: 1
                        }, children: [_jsx(Typography, { textAlign: 'left', width: '100%', fontFamily: 'Jost, sans-serif', textTransform: 'uppercase', color: theme.palette.primary.darker, sx: {
                                    fontWeight: 500,
                                    fontSize: { xs: '20px', sm: '20px', md: '28px', lg: '32px' },
                                    lineHeight: '100%'
                                }, children: t('eventRegistration.faqs') }), _jsxs(Typography, { variant: 'subtitle2', lineHeight: '16px', textAlign: 'left', mb: 2, width: '100%', color: theme.palette.primary.darker, sx: { display: 'inline' }, children: [t('eventRegistration.faqsSection.faqSubheading'), _jsx(Typography, { onClick: () => {
                                            onClose();
                                            onContactUsOpen();
                                        }, component: 'span', sx: {
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            color: theme.palette.primary.light,
                                            display: 'inline',
                                            ml: 0.5
                                        }, children: t('eventRegistration.faqsSection.contactLink') }), _jsxs(Typography, { component: 'span', sx: { display: 'inline', ml: 0.5 }, children: [t('eventRegistration.faqsSection.us'), "."] })] })] }), _jsx(Box, { sx: { mb: 3 }, children: _jsx(TextField, { fullWidth: true, value: searchQuery, onChange: (event) => setSearchQuery(event.target.value), placeholder: t('placeholders.search'), variant: 'filled', hiddenLabel: true, slotProps: {
                                input: {
                                    endAdornment: (_jsx(IconButton, { sx: {
                                            background: '#00244D1A',
                                            color: '#00244D',
                                            width: 48,
                                            height: 40,
                                            borderRadius: 1
                                        }, children: _jsx(SearchNormal1, { color: '#00244D', style: { width: 20, height: 20 } }) }))
                                }
                            }, sx: {
                                '& .MuiFilledInput-root': {
                                    borderRadius: 2,
                                    border: '1px solid #00244D33',
                                    backgroundColor: 'transparent',
                                    paddingTop: '0px !important',
                                    '&:before, &:after': { display: 'none' }
                                }
                            } }) }), showLoadingSkeleton ? (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [0, 1, 2].map((sectionIndex) => (_jsxs(Box, { mb: 2, children: [_jsx(Skeleton, { variant: 'text', width: '40%', height: 32, sx: { mb: 1.5, transform: 'none' } }), [0, 1].map((faqIndex) => (_jsx(Box, { sx: {
                                        border: '1px solid #00244D33',
                                        borderRadius: '8px',
                                        px: 2,
                                        py: 1.5,
                                        mb: 1
                                    }, children: _jsxs(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2
                                        }, children: [_jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Skeleton, { variant: 'text', width: '88%', height: 24, sx: { transform: 'none' } }), _jsx(Skeleton, { variant: 'text', width: '62%', height: 24, sx: { transform: 'none' } })] }), _jsx(Skeleton, { variant: 'rounded', width: 40, height: 40, sx: { borderRadius: 1 } })] }) }, `${sectionIndex}-${faqIndex}`)))] }, sectionIndex))) })) : filteredFaqData.length === 0 ? (_jsx(Box, { sx: { textAlign: 'center', py: 4 }, children: _jsx(Typography, { variant: 'subtitle1', color: '#00244D', fontWeight: 400, children: t('eventRegistration.faqsSection.noResults') }) })) : (filteredFaqData.map((section, sectionIndex) => (_jsxs(Box, { mb: 4, children: [_jsx(Typography, { variant: 'h6', fontWeight: 600, color: '#00244D', gutterBottom: true, sx: { mb: 2 }, children: section.title }), section.faqs.map((faq, faqIndex) => {
                                const globalIndex = `${sectionIndex}-${faqIndex}`;
                                const isExpanded = expandedIndex === globalIndex;
                                return (_jsxs(Accordion, { expanded: isExpanded, onChange: () => setExpandedIndex(isExpanded ? false : globalIndex), style: faqAccordionCardStyle, sx: {
                                        py: 0.5,
                                        px: 1,
                                        '&:before': { display: 'none' },
                                        '&.Mui-expanded': {
                                            backgroundColor: 'transparent',
                                            boxShadow: 'none',
                                            minHeight: 35
                                        }
                                    }, children: [_jsx(AccordionSummary, { expandIcon: undefined, "aria-controls": `panel${globalIndex}-content`, id: `panel${globalIndex}-header`, sx: {
                                                pl: 2,
                                                pr: 1,
                                                py: 1.5,
                                                minHeight: 'unset',
                                                backgroundColor: 'transparent',
                                                '& .MuiAccordionSummary-content': {
                                                    my: 0,
                                                    alignItems: 'center'
                                                },
                                                '&.Mui-expanded': {
                                                    minHeight: 35,
                                                    margin: 0,
                                                    '& .MuiAccordionSummary-content': {
                                                        margin: 0,
                                                        paddingTop: '8px'
                                                    }
                                                }
                                            }, children: _jsxs(Box, { sx: { ...between, gap: 1 }, children: [_jsx(Typography, { variant: 'subtitle1', color: '#00244D', fontWeight: 500, component: 'span', children: faq.question }), _jsx(IconButton, { sx: {
                                                            width: { xs: 32, sm: 36.57, md: 36.57, lg: 48 },
                                                            height: { xs: 28, sm: 32, md: 32, lg: 40 },
                                                            border: '1px solid #00244D99',
                                                            color: '#00244D',
                                                            borderRadius: { xs: 0.45, sm: 0.45, md: 0.45, lg: 1 },
                                                            flexShrink: 0
                                                        }, children: isExpanded ? (_jsx(Minus, { color: '#00244D', style: { width: 20, height: 20 } })) : (_jsx(Add, { color: '#00244D', style: { width: 20, height: 20 } })) })] }) }), _jsxs(AccordionDetails, { children: [_jsx(Divider, {}), _jsx(Typography, { variant: 'subtitle1', mt: 1, fontWeight: 400, color: '#00244D', sx: { '& a': { color: 'primary.main' } }, dangerouslySetInnerHTML: { __html: faq.answer } })] })] }, faq.id ?? globalIndex));
                            })] }, section.title))))] })] }));
    if (embeddedPreview) {
        return (_jsx(EmbeddedPreviewSheet, { open: open, onClose: onClose, previewLayout: previewLayout, children: content }));
    }
    return (_jsx(Drawer, { anchor: 'right', open: open, onClose: onClose, SlideProps: eventRegistrationDrawerSlideProps, PaperProps: getEventRegistrationDrawerPaperProps(false), children: content }));
};
export default EventRegistrationFaqsDrawer;
